/**
 * Helpers for Polylang translation data from WPGraphQL Polylang.
 * Used to build language-switcher URLs from WordPress Page/Post translations.
 */

/**
 * Normalize a URI or path from WordPress to a frontend path (leading slash, no trailing slash).
 * @param {string} [uri] - e.g. "careers/", "/rekry/", "blog/some-post/"
 * @returns {string} - e.g. "/careers", "/rekry", "/blog/some-post"
 */
export function normalizeTranslationPath(uri) {
  if (uri == null || typeof uri !== 'string') return '';
  const trimmed = uri.trim().replace(/^\/+|\/+$/g, '') || '';
  return trimmed ? `/${trimmed}` : '';
}

/**
 * Get language code for matching (handles 'en', 'en_GB', 'fi', 'fi_FI', etc.).
 * @param {string} [code]
 * @returns {string}
 */
function langCode(code) {
  return (code ?? '').toLowerCase();
}

/**
 * Pick the translation whose language code starts with 'fi'.
 * @param {Array<{ language?: { code?: string }, uri?: string, slug?: string }>} translations
 * @returns {{ uri?: string, slug?: string }|null}
 */
export function getFinnishTranslation(translations) {
  if (!Array.isArray(translations)) return null;
  return translations.find((t) => langCode(t?.language?.code).startsWith('fi')) ?? null;
}

/**
 * Pick the translation whose language code starts with 'en'.
 * @param {Array<{ language?: { code?: string }, uri?: string, slug?: string }>} translations
 * @returns {{ uri?: string, slug?: string }|null}
 */
export function getEnglishTranslation(translations) {
  if (!Array.isArray(translations)) return null;
  return translations.find((t) => langCode(t?.language?.code).startsWith('en')) ?? null;
}

/**
 * Build FI and EN switcher URLs from a node that has Polylang language + translations.
 * Used for WordPress Page and Post (blog, case studies already use slug-based logic elsewhere).
 *
 * @param {{
 *   language?: { code?: string },
 *   translations?: Array<{ language?: { code?: string }, uri?: string, slug?: string, link?: string }>,
 *   uri?: string,
 *   slug?: string
 * }} node - Page or Post from GraphQL (with language + translations when WPGraphQL Polylang is active)
 * @param {{ basePathFi?: string, basePathEn?: string }} [options] - For posts: base path for FI/EN (e.g. /blogi, /blog). Omit for pages (use uri as path).
 * @returns {{ fiUrl: string, enUrl: string }|null} - URLs for the language switcher, or null if not enough data
 */
export function getTranslationUrlsFromNode(node, options = {}) {
  if (!node) return null;
  const translations = node.translations ?? [];
  const currentCode = langCode(node.language?.code);
  const isCurrentFi = currentCode.startsWith('fi');
  const isCurrentEn = currentCode.startsWith('en');

  const fiTr = getFinnishTranslation(translations);
  const enTr = getEnglishTranslation(translations);

  const { basePathFi = '', basePathEn = '' } = options;
  const useBasePaths = basePathFi !== '' || basePathEn !== '';

  const toPath = (tr, isFi) => {
    if (!tr) return null;
    if (useBasePaths && tr.slug) {
      const base = isFi ? basePathFi : basePathEn;
      return base ? `${base.replace(/\/$/, '')}/${tr.slug}` : `/${tr.slug}`;
    }
    return normalizeTranslationPath(tr.uri ?? tr.link ?? (tr.slug ? `/${tr.slug}` : ''));
  };

  const currentPath = normalizeTranslationPath(node.uri ?? (node.slug ? `/${node.slug}` : ''));

  const fiUrl =
    (isCurrentFi ? currentPath : null) ?? (fiTr ? toPath(fiTr, true) : null) ?? (useBasePaths ? basePathFi : '');
  const enUrl =
    (isCurrentEn ? currentPath : null) ?? (enTr ? toPath(enTr, false) : null) ?? (useBasePaths ? basePathEn : '');

  if (!fiUrl && !enUrl) return null;
  return {
    fiUrl: fiUrl || (useBasePaths ? basePathFi : '/blogi'),
    enUrl: enUrl || (useBasePaths ? basePathEn : '/blog'),
  };
}
